import React, { MutableRefObject, ReactElement, Ref, forwardRef, useImperativeHandle, useMemo, useRef } from "react";
import { getChromeMajorVersion } from "react-lib/utils/chromeVersion";

type ExaminationImageOverlayProps = {
  children: ReactElement;
  clinicalFindings: Record<string, any>[];
  filterClinicalFindingIds: number[];
  firstPosition?: boolean;
  historyReviewSubTab: number;
  imageKey: string;
  showDrawingChecked: number[];
  sub: string;

  // config
  height?: string;
  hideTooltip?: boolean;
  isNeedToggle?: boolean;
  width?: string;
};

type ShowParams = {
  content?: (organ: Record<string, any>) => string;
  key: string;
  organ?: Record<string, any>;
  selector: string;
  zoomElement?: Element | null;
  target: SVGElement;
  masterData?: Record<string, any>;
};

const show = (self: HTMLDivElement, params: ShowParams) => {
  const findOrgan = () => {
    if (params.organ) {
      return params.organ;
    }

    const organItems: Record<string, any>[] = params.masterData?.organ?.items || [];

    const organMaster = organItems.filter((item) => Object.hasOwn(item, "settingKey"));

    return organMaster.find((item: any) => item.settingKey === params.key);
  };

  const organ = findOrgan();

  if (!organ) {
    return;
  }

  // issue 53771
  const svgZoom = (params.zoomElement || (params.selector ? document.querySelector(params.selector) : null)) as HTMLFormElement | null;

  let zoom = 1;

  if (svgZoom) {
    const compStyles = window.getComputedStyle(svgZoom);

    zoom = Number.parseFloat(compStyles.getPropertyValue("zoom"));
  }

  let chromeVersion = "";

  let { height, left, right, top } = params.target.getBoundingClientRect();

  const chromeMajorVersion = getChromeMajorVersion();

  if (chromeMajorVersion > 0 && chromeMajorVersion < 125) {
    // issue 52820
    top *= zoom;
    left *= zoom;
    height *= zoom;
    right *= zoom;
  }

  self.innerHTML = params.content ? params.content(organ) : organ.nickName;
  self.style.display = "";

  // fixed จะอ้างอิง ancestor ที่มี transform / will-change (เช่น .ui.modal) แทนจอ จึงหักจุดเริ่มของ container ออก
  self.style.top = "0px";
  self.style.left = "0px";

  const origin = self.getBoundingClientRect();

  self.style.top = `${top + height + 10 - origin.top}px`;

  const { width } = origin;
  const center = (left + right) / 2 - width / 2;

  self.style.left = `${center - origin.left}px`;
};

const ExaminationImageOverlay = (props: ExaminationImageOverlayProps, ref: Ref<any>) => {
  const tooltipRef = useRef<HTMLDivElement>(null);

  useImperativeHandle<any, any>(ref, () => ({
    current: tooltipRef.current,
    show: (params: any) => {
      if (tooltipRef.current) {
        show(tooltipRef.current, params);
      }
    },
  }));

  const filteredClinicalFindings = useMemo(() => {
    if (props.sub === "DiagnosisHistory" && props.historyReviewSubTab === 0) {
      return props.clinicalFindings;
    } else if (props.sub === "DiagnosisHistory" && props.historyReviewSubTab === 1 && props.isNeedToggle) {
      return props.clinicalFindings.filter((item) => props.filterClinicalFindingIds.includes(item.id) && props.showDrawingChecked.includes(item.id));
    } else if (props.sub === "TreatmentHistory") {
      return props.clinicalFindings.filter((item) => props.filterClinicalFindingIds.includes(item.id));
    }

    return props.clinicalFindings.filter((item) => props.filterClinicalFindingIds.includes(item.id) && props.showDrawingChecked.includes(item.id));
  }, [props.clinicalFindings, props.filterClinicalFindingIds, props.historyReviewSubTab, props.isNeedToggle, props.showDrawingChecked, props.sub]);

  return (
    <>
      {props.children}
      {/* alway show if in VisitTab under History review */}
      {filteredClinicalFindings.map((data, index) => {
        const shouldShowImage =
          ["DiagnosisHistory", "TreatmentHistory"].includes(props.sub) && props.historyReviewSubTab === 0 ? true : props.showDrawingChecked.includes(data.id);

        const image: string = data.image?.[props.imageKey] || "";

        return (
          shouldShowImage &&
          image && (
            <img
              key={`${props.imageKey}-${index}`}
              alt={`${props.imageKey}-${index}`}
              height={props.height || "400"}
              src={image}
              width={props.width || "1200"}
              style={{
                pointerEvents: "none",
                position: "absolute",
                ...(props.firstPosition ? { left: 0, top: 0 } : {}),
              }}
            />
          )
        );
      })}

      {!props.hideTooltip && <Tooltip ref={tooltipRef} />}
    </>
  );
};

export const Tooltip = React.forwardRef<any, any>((props, ref) => {
  const tooltipRef = useRef() as MutableRefObject<HTMLDivElement>;

  useImperativeHandle<any, any>(ref, () => ({
    current: tooltipRef.current,
    show: (params: any) => {
      show(tooltipRef.current, params);
    },
  }));

  return (
    <div
      ref={tooltipRef}
      style={{
        backgroundColor: "black",
        borderRadius: "5px",
        color: "white",
        display: "none",
        left: "334px",
        maxWidth: "150px",
        padding: "6px",
        position: "fixed",
        top: "109px",
      }}
    />
  );
});

Tooltip.displayName = "Tooltip";

const ForwardedExaminationImageOverlay = forwardRef<any, ExaminationImageOverlayProps>(ExaminationImageOverlay);

ExaminationImageOverlay.displayName = "ExaminationImageOverlay";

export default React.memo(ForwardedExaminationImageOverlay);
